import React, { useMemo } from 'react';
import { useFetch } from '../hooks/useFetch';
import StatusBanner from './ui/StatusBanner';
import SkeletonBlock from './ui/SkeletonBlock';
import { formatTimestamp } from '../utils/formatDate';

const TIMESTAMP_FALLBACK = 'Unknown time';

function formatStatusTransition(entry) {
  if (entry.oldStatus && entry.newStatus) {
    return `${entry.oldStatus} -> ${entry.newStatus}`;
  }

  return entry.newStatus || entry.oldStatus || 'Status updated';
}

function EventHistoryPanel({ eventId, title = 'Request History', refreshKey = 0, requestContext = null }) {
  const url = eventId ? `/api/events/${eventId}/history` : null;
  const { data, isLoading, error, refetch } = useFetch(url, {
    deps: [eventId, refreshKey],
    initialData: []
  });

  const historyEntries = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const errorMessage = error || '';

  if (!eventId) {
    return (
      <div className="role-history-panel role-history-empty">
        <h3>{title}</h3>
        <p>Select a request to view approval and denial history.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="role-history-panel">
        <div className="role-history-header">
          <h3>{title}</h3>
          <span>Loading…</span>
        </div>
        <div className="role-history-list" aria-label="Loading…" aria-live="polite">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="role-history-item" key={index}>
              <div className="role-history-topline">
                <SkeletonBlock width="34%" height={12} />
                <SkeletonBlock width={92} height={12} />
              </div>
              <SkeletonBlock width="72%" height={12} style={{ marginTop: 10 }} />
              <SkeletonBlock width="52%" height={10} style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="role-history-panel role-history-error">
        <h3>{title}</h3>
        <StatusBanner kind="error" message={errorMessage} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="role-history-panel">
      {requestContext ? (
        <div className="role-history-context-bar">
          <i className="fas fa-info-circle" aria-hidden="true" />
          <span>History for: <strong>{requestContext}</strong></span>
        </div>
      ) : null}
      <div className="role-history-header">
        <h3>{title}</h3>
        <span>{historyEntries.length} entries</span>
      </div>
      {historyEntries.length > 0 ? (
        <div className="role-history-list">
          {historyEntries.map(entry => (
            <div className="role-history-item" key={entry.id}>
              <div className="role-history-topline">
                <strong>{entry.action || 'update'}</strong>
                <span>{formatTimestamp(entry.changedAt, TIMESTAMP_FALLBACK)}</span>
              </div>
              <p className="role-history-transition">{formatStatusTransition(entry)}</p>
              <p className="role-history-meta">Event: {entry.eventType || 'vacation_request'}</p>
              {entry.reason && (
                <p className="role-history-reason">Reason: {entry.reason}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="role-history-empty-copy">No items found. Approval and denial history will appear here after a decision is recorded.</p>
      )}
    </div>
  );
}

export default EventHistoryPanel;
